import React from 'react';
import { QuestionProps } from '.';
import {
  AIWorkflow,
  MESSAGE_CONTEXT_TYPES,
} from '../../../contexts/ai-workflow';
import { ACTIVITY_STEP } from '../Content';
import {
  ACTIVITY_ACTION,
  ActivityContext,
  ActivityContextMultipleChoiceQuestionAnswer,
} from '../../../contexts/activity';
import { toast } from 'react-toastify';
import IncorrectAnswerToast from '../../Toasts/TryAgain';
import { BottomSpacer, getQuestionText, SplitScreenQuestion } from './common';
import { ReadableContainer, ReadableMarkdown } from '../Containers';
import { H1 } from '../../elements/Text';
import MentorReview from '../MentorComment';
import PythonPlayground from '../../PythonPlayground';
import MultipleChoiceSelector from './MultipleChoiceSelector';

export default function PythonMultipleChoiceQuestion({
  question,
  answer,
}: QuestionProps & {
  answer: ActivityContextMultipleChoiceQuestionAnswer;
}) {
  const { setMessageContext } = React.useContext(AIWorkflow);
  const [activity, dispatch] = React.useContext(ActivityContext);

  const [selectedAnswer, setSelectedAnswer] = React.useState<string | null>(
    answer.value
  );

  const qIndex =
    activity.currentStep?.type === ACTIVITY_STEP.QUESTION
      ? activity?.currentStep?.questionIndex
      : -1;

  const onCodeChange = React.useCallback(
    (code: string) => {
      dispatch({
        type: ACTIVITY_ACTION.CHANGE_CODE_MULTIPLE_CHOICE_QUESTION,
        payload: {
          question,
          code,
        },
      });
    },
    [dispatch, question]
  );

  const onHint = React.useCallback(() => {
    setMessageContext({
      context_type: MESSAGE_CONTEXT_TYPES.QUESTION_HINT,
      context_content: {
        question_index: qIndex,
        attempted_answer: answer.value ?? '',
        working_code: answer.working_code ?? '',
        is_correct: answer.isCorrect,
      },
    });
  }, [answer, qIndex, setMessageContext]);

  React.useEffect(() => {
    if (answer.value && answer.value !== selectedAnswer) {
      setSelectedAnswer(answer.value);
      if (answer.isCorrect) {
        toast.success('Correct!');
      } else if (answer.isCorrect == false) {
        toast.warn(() => <IncorrectAnswerToast onHint={onHint} />);
      }
    }
  }, [answer, onHint, selectedAnswer]);

  const onResult = React.useCallback(
    ({result}: {result: string, code: string, shouldVerifyResult: boolean}) => {
      dispatch({
        // TODO: this should be a separate action which doesnt set isAnswered nor isCorrect as the answer comes from the multiple choice options
        type: ACTIVITY_ACTION.SET_RESULT_PYTHON_PLAYGROUND_QUESTION,
        payload: {
          question,
          result,
          shouldVerifyResult: false,
        },
      });
    },
    [dispatch, question]
  );

  const onResetCode = React.useCallback(() => {
    dispatch({
      type: ACTIVITY_ACTION.CHANGE_CODE_MULTIPLE_CHOICE_QUESTION,
      payload: {
        question,
        code: question.initial_py ?? '',
      },
    });
  }, [dispatch, question]);

  const mentorComment =
    activity.latestSavedSubmission?.mentor_reviews?.find(
      (review) => review.questionId === question.id
    )?.review ?? null;
  const onAnswerChange = React.useCallback(
    (option: string) => {
      dispatch({
        type: ACTIVITY_ACTION.CHANGE_ANSWER_MULTIPLE_CHOICE_QUESTION,
        payload: {
          question,
          answer: option,
        },
      });
    },
    [dispatch, question]
  );

  return (
    <SplitScreenQuestion>
      <>
        <ReadableContainer>
          <H1>Exercise {qIndex + 1}</H1>
          {!!mentorComment && <MentorReview comment={mentorComment} />}
        </ReadableContainer>
        <ReadableMarkdown md={getQuestionText(question)} />
        <MultipleChoiceSelector
          question={question}
          onAnswerChange={onAnswerChange}
          selectedAnswer={answer.value}
        />
        <BottomSpacer />
      </>
      <PythonPlayground
        isRunOnly
        setupPy={question.setup_py ?? ''}
        code={answer.working_code ?? ''}
        onCodeChange={onCodeChange}
        onResult={onResult}
        onResetCode={onResetCode}
      />
    </SplitScreenQuestion>
  );
}
